<template>
  <v-container fluid>
    <v-snackbar v-model="snackbar" :top="true" :color="color" :timeout="6000">
      <span v-if="snackbar_msg != null">{{ snackbar_msg }}</span>
      <v-btn dark text @click="snackbar = false">Close</v-btn>
    </v-snackbar>

    <div v-if ="isallowed == true">
    <v-row>
      <!-- Canvas Area -->
      <v-col cols="12" md="8" class="pa-5" style="background: #f3f3f3; position: relative">
        <!-- Page Switch -->
        <v-btn-toggle v-model="activePage" mandatory class="mb-3">
          <v-btn value="front">Front</v-btn>
          <v-btn value="back">Back</v-btn>
        </v-btn-toggle>

        <!-- FRONT SIDE -->
        <div v-if="activePage === 'front'" ref="canvasFront" :style="canvasStyle" @mousedown="canvasClick">
          <div v-for="el in pages.front.elements" :key="el.id" class="designer-element" :style="getElementStyle(el)"
            @mousedown.stop="startDrag($event, el)" @dblclick="selectElement(el)">
            <span v-if="el.type === 'text'" :style="textStyle(el)">
              {{ el.text }}
            </span>

            <img v-else-if="el.type === 'image'" :src="el.src" :style="{
              width: '100%',
              height: '100%',
              objectFit: el.fit || 'cover',
            }" />

            <vue-barcode v-else-if="el.type === 'barcode'" :value="el.text" :width="el.barWidth || 2"
              :height="el.barHeight || el.height || 40"
              :display-value="el.displayValue != null ? el.displayValue : false" :background="el.bgColor || '#ffffff'"
              :line-color="el.barColor || '#000000'" :margin="0"
              style="width: 100%; height: 100%; padding:0; margin:0; box-sizing:border-box;" />

            <qrcode-vue v-else-if="el.type === 'qrcode'" :value="el.text" :size="el.qrSize || 100"
              :background="el.bgColor || '#ffffff'" :foreground="el.qrColor || '#000000'" level="H"
              style="width:100%; height:100%;" />

            <div class="resize-handle" @mousedown.stop="startResize($event, el)"></div>

            <div v-if="selectedElement && selectedElement.id === el.id" class="size-display">
              {{ el.width.toFixed(1) }}mm x {{ el.height.toFixed(1) }}mm
            </div>
          </div>
        </div>

        <!-- BACK SIDE -->
        <div v-if="activePage === 'back'" ref="canvasBack" :style="canvasStyle" @mousedown="canvasClick">
          <div v-for="el in pages.back.elements" :key="el.id" class="designer-element" :style="getElementStyle(el)"
            @mousedown.stop="startDrag($event, el)" @dblclick="selectElement(el)">
            <span v-if="el.type === 'text'" :style="textStyle(el)">
              {{ el.text }}
            </span>

            <img v-else-if="el.type === 'image'" :src="el.src" :style="{
              width: '100%',
              height: '100%',
              objectFit: el.fit || 'cover',
            }" />

            <vue-barcode v-else-if="el.type === 'barcode'" :value="el.text" :width="el.barWidth || 2"
              :height="el.barHeight || 40" :display-value="el.displayValue != null ? el.displayValue : false"
              :background="el.bgColor || '#fff'" :line-color="el.barColor || '#000'"
              style="width: 100%; height: 100%;" />

            <qrcode-vue v-else-if="el.type === 'qrcode'" :value="el.text" :size="el.qrSize || 100"
              :background="el.bgColor || '#ffffff'" :foreground="el.qrColor || '#000000'" level="H"
              style="width:100%; height:100%;" />

            <div class="resize-handle" @mousedown.stop="startResize($event, el)"></div>

            <div v-if="selectedElement && selectedElement.id === el.id" class="size-display">
              {{ el.width.toFixed(1) }}mm x {{ el.height.toFixed(1) }}mm
            </div>
          </div>
        </div>
      </v-col>

      <!-- Properties Drawer -->
      <v-navigation-drawer v-model="drawer" width="360" absolute permanent right class="elevation-2 modern-drawer">
        <div class="pa-4">
          <div class="d-flex align-center justify-space-between mb-3">
            <h3 class="text-h6 font-weight-medium">
              {{ selectedElement ? "Element Properties" : "Page Properties" }}
            </h3>
            <v-icon size="20">mdi-tune-variant</v-icon>
          </div>
          <v-divider class="mb-4"></v-divider>

          <!-- ==================== ELEMENT PROPERTIES ==================== -->
          <template v-if="selectedElement">
            <!-- TEXT ELEMENT -->
            <v-text-field v-if="selectedElement.type === 'text'" v-model="selectedElement.text" label="Text" outlined
              dense class="mb-3" @input="saveHistory" />

            <v-autocomplete v-if="selectedElement.type === 'text'" :items="placeholderFields" item-text="text"
              item-value="value" v-model="selectedPlaceholder" label="Insert Placeholder" outlined dense class="mb-3"
              @change="insertPlaceholder" />

            <!-- alignment left right center -->
            <v-btn-toggle class="mb-3 mr-3" v-if="selectedElement.type === 'text'" v-model="selectedElement.alignment"
              dense mandatory @change="saveHistory">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-bind="attrs" v-on="on" value="left" icon>
                    <v-icon>mdi-format-align-left</v-icon>
                  </v-btn>
                </template>
                <span>Align Left</span>
              </v-tooltip>

              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-bind="attrs" v-on="on" value="center" icon>
                    <v-icon>mdi-format-align-center</v-icon>
                  </v-btn>
                </template>
                <span>Align Center</span>
              </v-tooltip>

              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-bind="attrs" v-on="on" value="right" icon>
                    <v-icon>mdi-format-align-right</v-icon>
                  </v-btn>
                </template>
                <span>Align Right</span>
              </v-tooltip>
            </v-btn-toggle>

            <!-- Vertical Alignment -->
            <v-btn-toggle class="mb-3" v-if="selectedElement.type === 'text'"
              v-model="selectedElement.verticalAlignment" dense mandatory @change="saveHistory">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-bind="attrs" v-on="on" value="top" icon>
                    <v-icon>mdi-format-vertical-align-top</v-icon>
                  </v-btn>
                </template>
                <span>Align Top</span>
              </v-tooltip>

              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-bind="attrs" v-on="on" value="center" icon>
                    <v-icon>mdi-format-vertical-align-center</v-icon>
                  </v-btn>
                </template>
                <span>Align Middle</span>
              </v-tooltip>

              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-bind="attrs" v-on="on" value="bottom" icon>
                    <v-icon>mdi-format-vertical-align-bottom</v-icon>
                  </v-btn>
                </template>
                <span>Align Bottom</span>
              </v-tooltip>
            </v-btn-toggle>

            <v-btn-toggle v-if="selectedElement.type === 'text'" v-model="selectedElement.textTransform" dense mandatory
              class="mb-3 mr-3" @change="saveHistory">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-bind="attrs" v-on="on" value="uppercase" icon>
                    <v-icon>mdi-format-letter-case-upper</v-icon>
                  </v-btn>
                </template>
                <span>Uppercase</span>
              </v-tooltip>

              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-bind="attrs" v-on="on" value="lowercase" icon>
                    <v-icon>mdi-format-letter-case-lower</v-icon>
                  </v-btn>
                </template>
                <span>Lowercase</span>
              </v-tooltip>

              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-bind="attrs" v-on="on" value="capitalize" icon>
                    <v-icon>mdi-format-letter-starts-with</v-icon>
                  </v-btn>
                </template>
                <span>Capitalize</span>
              </v-tooltip>
            </v-btn-toggle>

            <!-- text color -->
            <v-color-picker v-if="selectedElement.type === 'text'" v-model="selectedElement.color" hide-canvas
              show-swatches class="mb-6" @input="saveHistory" />

            <!-- font size -->
            <span v-if="selectedElement.type === 'text'">
              Font Size: ({{ selectedElement.fontSize }})
            </span>
            <v-slider v-if="selectedElement.type === 'text'" v-model="selectedElement.fontSize" step="1" min="4"
              max="48" @change="saveHistory" />

            <!-- Bold + Italic Buttons -->
            <div v-if="selectedElement.type === 'text'" class="d-flex mb-3">
              <v-btn outlined small class="mb-2 mr-2" :color="selectedElement.bold ? 'primary' : ''"
                @click="selectedElement.bold = !selectedElement.bold; saveHistory();">
                <v-icon small left>mdi-format-bold</v-icon>
              </v-btn>

              <v-btn outlined small class="mb-2 mr-2" :color="selectedElement.italic ? 'primary' : ''"
                @click="selectedElement.italic = !selectedElement.italic; saveHistory();">
                <v-icon small left>mdi-format-italic</v-icon>
              </v-btn>

              <v-btn outlined small class="mb-2 mr-2" :color="selectedElement.underline ? 'primary' : ''"
                @click="selectedElement.underline = !selectedElement.underline; saveHistory();">
                <v-icon small left>mdi-format-underline</v-icon>
              </v-btn>
            </div>

            <v-select v-if="selectedElement.type === 'text'" :items="fonts" v-model="selectedElement.fontFamily"
              label="Font Family" outlined dense class="mb-3" @change="saveHistory" />

            <!-- Position & Size -->
            <div class="d-flex flex-wrap" style="gap:8px;">
              <v-text-field v-model.number="selectedElement.top" label="Top (mm)" outlined dense class="flex-grow-1"
                @input="saveHistory" />
              <v-text-field v-model.number="selectedElement.left" label="Left (mm)" outlined dense class="flex-grow-1"
                @input="saveHistory" />
            </div>

            <div class="d-flex flex-wrap mt-2" style="gap:8px;">
              <v-text-field v-model.number="selectedElement.width" label="Width (mm)" outlined dense class="flex-grow-1"
                @input="saveHistory" />
              <v-text-field v-model.number="selectedElement.height" label="Height (mm)" outlined dense
                class="flex-grow-1" @input="saveHistory" />
            </div>

            <!-- IMAGE ELEMENT -->
            <v-select v-if="selectedElement.type === 'image'" :items="['cover', 'contain']"
              v-model="selectedElement.fit" label="Image Fit" outlined dense class="mb-3" @change="saveHistory" />

            <div v-if="selectedElement.type === 'image'">
              <v-select outlined dense class="mb-3" label="Photo Type" @change="saveHistory"
                v-model="selectedElement.tag"
                :items="['Normal', 'UserPhoto', 'StudentSign', 'FatherPhoto', 'MotherPhoto','PrincipalSign','EmployeePhoto','EmployeeSign']"></v-select>
            </div>

            <v-text-field v-if="selectedElement.type === 'image'" v-model.number="selectedElement.borderWidth"
              label="Border Width (px)" outlined dense class="mb-3" @input="saveHistory" />

            <span v-if="selectedElement.type === 'image'" class="text-grey caption mb-1">Border Color</span>
            <v-color-picker v-if="selectedElement.type === 'image'" v-model="selectedElement.borderColor" hide-canvas
              show-swatches class="mb-3" @input="saveHistory" />

            <v-slider v-if="selectedElement.type === 'image'" v-model.number="selectedElement.borderRadius"
              label="Border Radius (px)" min="0" max="50" step="1" class="mb-3" @change="saveHistory" />

            <!-- SHAPE ELEMENT -->
            <span v-if="selectedElement.type === 'shape'" class="text-grey caption">Fill Color</span>
            <v-color-picker v-if="selectedElement.type === 'shape'" v-model="selectedElement.fillColor" hide-canvas
              show-swatches class="mb-3" @input="saveHistory" />

            <span v-if="selectedElement.type === 'shape'" class="text-grey caption">Border Color</span>
            <v-color-picker v-if="selectedElement.type === 'shape'" v-model="selectedElement.borderColor" hide-canvas
              show-swatches class="mb-3" @input="saveHistory" />

            <v-slider v-if="selectedElement.type === 'shape'" v-model="selectedElement.borderWidth" label="Border Width"
              min="0" max="10" step="1" class="mb-3" @change="saveHistory" />

            <v-slider v-if="selectedElement.type !== 'qrcode'" v-model="selectedElement.opacity" label="Opacity" min="0"
              max="1" step="0.1" class="mb-3" @change="saveHistory" />

            <v-slider v-model.number="selectedElement.rotation" label="Rotation (°)" min="0" max="360" step="1"
              class="mb-2" @change="saveHistory" />

            <v-text-field v-if="selectedElement.type === 'barcode'" v-model="selectedElement.text" label="Barcode Value"
              outlined dense class="mb-2" @input="saveHistory" />

            <v-slider v-if="selectedElement.type === 'barcode'" v-model="selectedElement.barWidth" label="Bar Width"
              min="0.1" max="5" step="0.1" hide-details></v-slider>

            <v-slider v-if="selectedElement.type === 'barcode'" v-model="selectedElement.barHeight" label="Bar Height"
              min="10" max="100" step="0.1" hide-details></v-slider>

            <v-switch v-if="selectedElement.type === 'barcode'" v-model="selectedElement.displayValue"
              label="Show Text Below Barcode" class="mb-2" @change="saveHistory" />

            <v-color-picker v-if="selectedElement.type === 'barcode'" v-model="selectedElement.barColor" hide-canvas
              show-swatches label="Bar Color" class="mb-2" @input="saveHistory" />

            <v-color-picker v-if="selectedElement.type === 'barcode'" v-model="selectedElement.bgColor" hide-canvas
              show-swatches label="Background Color" class="mb-2" @input="saveHistory" />
          </template>

          <!-- QR Code Properties (outside template v-if to match original placement) -->
          <v-text-field v-if="selectedElement && selectedElement.type === 'qrcode'" v-model="selectedElement.text"
            label="QR Value" outlined dense class="mb-2" @input="saveHistory" />

          <v-slider v-if="selectedElement && selectedElement.type === 'qrcode'" v-model="selectedElement.qrSize"
            label="QR Density" min="20" max="400" step="1" class="mb-2" @change="saveHistory" />

          <v-color-picker v-if="selectedElement && selectedElement.type === 'qrcode'" v-model="selectedElement.qrColor"
            hide-canvas show-swatches label="QR Color" class="mb-2" @input="saveHistory" />

          <v-color-picker v-if="selectedElement && selectedElement.type === 'qrcode'" v-model="selectedElement.bgColor"
            hide-canvas show-swatches label="Background Color" class="mb-2" @input="saveHistory" />

          <!-- ==================== PAGE PROPERTIES ==================== -->
          <template v-if="!selectedElement">
            <v-text-field v-model.number="currentCard.width" label="Width (mm)" outlined dense class="mb-3"
              @input="saveHistory" />
            <v-text-field v-model.number="currentCard.height" label="Height (mm)" outlined dense class="mb-3"
              @input="saveHistory" />
            <v-text-field v-model="currentCard.bgColor" label="Background Color" outlined dense class="mb-3"
              @input="saveHistory" />
            <v-text-field v-model="currentCard.bgImage" label="Background Image URL" outlined dense class="mb-3"
              @input="saveHistory" />
            <div class="d-flex align-center justify-space-between mt-2">
              <small class="text-grey">or upload background image</small>
              <v-btn color="primary" @click="$refs.bgFileInput.click()" :loading="upload_bg_loading">
                <v-icon left>mdi-image-plus</v-icon> Upload
              </v-btn>
            </div>

            <input ref="bgFileInput" type="file" accept="image/*" style="display:none"
              @change="handleBackgroundUpload" />

            <v-btn v-if="currentCard.bgImage" color="error" text small @click="removeBackground">
              <v-icon left>mdi-delete</v-icon> Remove Background
            </v-btn>
          </template>

          <!-- ==================== LAYERS ==================== -->
          <v-divider class="my-4"></v-divider>

          <h3 class="subtitle-2 mb-2">Layers</h3>

          <div class="layer-list" style="max-height: 250px; overflow-y: auto;" @dragover.prevent>
            <div v-for="(el, index) in pages[activePage].elements" :key="el.id" class="layer-item"
              :class="{ active: selectedElement && selectedElement.id === el.id }" draggable="true"
              @dragstart="dragStart(index)" @dragenter.prevent="dragEnter(index)" @dragend="dragEnd"
              @click="selectElement(el)">
              <v-row align="center" no-gutters>
                <v-col cols="2">
                  <v-icon small @click.stop="toggleVisibility(el)">
                    {{ el.hidden ? 'mdi-eye-off' : 'mdi-eye' }}
                  </v-icon>
                </v-col>
                <v-col cols="8">
                  {{ el.layerName || (el.type + ' ' + el.id) }}
                </v-col>
                <v-col cols="2">
                  <v-icon small>mdi-drag</v-icon>
                </v-col>
              </v-row>
            </div>
          </div>

          <v-divider class="my-4"></v-divider>

          <!-- ==================== ICON TOOLBAR ==================== -->
          <div class="d-flex flex-wrap justify-center" style="gap:8px;">
            <!-- Add Text -->
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn v-bind="attrs" v-on="on" icon @click="addText">
                  <v-icon>mdi-format-letter-case</v-icon>
                </v-btn>
              </template>
              <span>Add Text</span>
            </v-tooltip>

            <!-- Add Image -->
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn v-bind="attrs" v-on="on" icon :loading="upload_image_loading" @click="$refs.fileInput.click()">
                  <v-icon>mdi-image-plus</v-icon>
                </v-btn>
              </template>
              <span>Add Image</span>
            </v-tooltip>
            <input type="file" ref="fileInput" style="display:none" @change="handleFile" />

            <!-- Add Shape -->
            <v-menu offset-y>
              <template v-slot:activator="{ on: menuOn, attrs: menuAttrs }">
                <v-tooltip top>
                  <template v-slot:activator="{ on: tipOn, attrs: tipAttrs }">
                    <v-btn v-bind="Object.assign({}, menuAttrs, tipAttrs)" v-on="Object.assign({}, menuOn, tipOn)" icon>
                      <v-icon>mdi-shape-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Add Shape</span>
                </v-tooltip>
              </template>
              <v-list dense>
                <v-list-item @click="addShape('rectangle')">
                  <v-icon left>mdi-rectangle-outline</v-icon>Rectangle
                </v-list-item>
                <v-list-item @click="addShape('circle')">
                  <v-icon left>mdi-circle-outline</v-icon>Circle
                </v-list-item>
                <v-list-item @click="addShape('line')">
                  <v-icon left>mdi-minus</v-icon>Line
                </v-list-item>
              </v-list>
            </v-menu>

            <!-- Add Barcode -->
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn v-bind="attrs" v-on="on" icon @click="addBarcode">
                  <v-icon>mdi-barcode</v-icon>
                </v-btn>
              </template>
              <span>Add Barcode</span>
            </v-tooltip>

            <!-- Add QR Code -->
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn v-bind="attrs" v-on="on" icon @click="addQRCode">
                  <v-icon>mdi-qrcode</v-icon>
                </v-btn>
              </template>
              <span>Add QR Code</span>
            </v-tooltip>
          </div>

          <v-divider class="my-4"></v-divider>

          <div class="d-flex flex-wrap justify-center" style="gap:8px;">
            <!-- Print -->
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn v-bind="attrs" v-on="on" icon color="success" @click="printIDCard()">
                  <v-icon>mdi-printer</v-icon>
                </v-btn>
              </template>
              <span>Print ID Card</span>
            </v-tooltip>

            <!-- Export -->
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn v-bind="attrs" v-on="on" icon color="info" @click="exportIDCard">
                  <v-icon>mdi-export-variant</v-icon>
                </v-btn>
              </template>
              <span>Export Design</span>
            </v-tooltip>

            <!-- Import -->
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn v-bind="attrs" v-on="on" icon color="warning" @click="$refs.importFile.click()">
                  <v-icon>mdi-import</v-icon>
                </v-btn>
              </template>
              <span>Import Design</span>
            </v-tooltip>
            <input type="file" ref="importFile" style="display:none" @change="importIDCard" />

            <!-- Save -->
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn v-bind="attrs" v-on="on" icon color="primary" @click="open_save_template_dialog">
                  <v-icon>mdi-content-save-outline</v-icon>
                </v-btn>
              </template>
              <span>Save Template</span>
            </v-tooltip>

            <!-- Delete -->
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn v-bind="attrs" v-on="on" icon color="error" @click="deleteElement">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </template>
              <span>Delete Element</span>
            </v-tooltip>
          </div>
        </div>
      </v-navigation-drawer>
    </v-row>

    <!-- Save Template Dialog -->
    <v-dialog width="520" v-model="template_name_dialog">
      <v-card class="pa-5 rounded-xl" elevation="12">
        <v-card-title class="text-h6 font-weight-bold">
          Save Template
        </v-card-title>
        <v-card-text>
          <v-text-field v-model="template_name" outlined class="mb-4" label="Template Name">
            <template v-slot:append>
              <span style="color:red">*</span>
            </template>
          </v-text-field>

          <v-btn block color="primary" large class="rounded-lg mb-3" @click="saveTemplate">
            <v-icon left>mdi-content-save-plus-outline</v-icon>
            Save as New Template
          </v-btn>

          <v-btn block color="success" large class="rounded-lg" @click="openUpdateTemplateDialog">
            <v-icon left>mdi-database-edit</v-icon>
            Update Existing Template
          </v-btn>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="template_name_dialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Update Existing Template Dialog -->
    <v-dialog v-model="update_dialog" max-width="480px">
      <v-card class="pa-6 rounded-xl" elevation="12">
        <v-card-title class="text-h6 font-weight-bold">
          Update Existing Template
        </v-card-title>
        <v-card-text>
          <v-select v-model="selected_update_template" :items="template_list" item-text="template_name" item-value="id"
            label="Select Template to Update" outlined class="mb-4"></v-select>

          <v-btn block color="success" large :disabled="!selected_update_template" @click="updateExistingTemplate">
            <v-icon left>mdi-content-save-edit</v-icon>
            Update Template
          </v-btn>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="update_dialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- New Design Dialog -->
    <v-dialog v-model="new_dialog" max-width="460px" persistent transition="dialog-bottom-transition">
      <v-card elevation="16" class="rounded-xl pa-6" style="
          backdrop-filter: blur(12px);
          background: rgba(255, 255, 255, 0.85);
          border: 1px solid #eee;
        ">
        <div class="text-center mb-4">
          <v-icon size="38" color="primary">mdi-badge-account</v-icon>
          <h2 class="text-h6 mt-2 font-weight-bold">Design ID Card</h2>
          <p class="body-2 text-medium-emphasis">
            Choose how you want to begin your design
          </p>
        </div>

        <v-card-text class="d-flex flex-column" style="gap:16px;">
          <v-btn block large color="primary" class="rounded-lg py-5 modern-btn" @click="createNewDesign">
            <v-icon left>mdi-plus-box-multiple</v-icon>
            Create New Design
          </v-btn>

          <v-btn block large color="info" class="rounded-lg py-5 modern-btn" @click="$refs.startImportInput.click()">
            <v-icon left>mdi-file-import</v-icon>
            Import Design from JSON
          </v-btn>

          <input type="file" ref="startImportInput" accept="application/json" style="display:none"
            @change="importIDCard" />

          <v-btn block large color="success" class="rounded-lg py-5 modern-btn" @click="loadFromERP">
            <v-icon left>mdi-database-search</v-icon>
            Load From Database
          </v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- ERP Template Selection Dialog -->
    <v-dialog v-model="erp_dialog" max-width="480px" transition="dialog-bottom-transition">
      <v-card elevation="16" class="rounded-xl pa-6" style="
          backdrop-filter: blur(12px);
          background: rgba(255, 255, 255, 0.88);
          border: 1px solid #e5e5e5;
        ">
        <div class="text-center mb-4">
          <v-icon size="38" color="success">mdi-database-search</v-icon>
          <h2 class="text-h6 mt-2 font-weight-bold">Load Template From Database</h2>
          <p class="body-2 text-medium-emphasis">
            Select a saved template from ERP
          </p>
        </div>

        <v-card-text class="pt-2">
          <v-select v-model="selected_erp_template" :items="template_list" item-text="template_name" item-value="id"
            label="Select Template" outlined dense class="mb-4"
            prepend-inner-icon="mdi-format-list-bulleted"></v-select>

          <v-btn color="success" block large class="rounded-lg py-4 modern-btn" :loading="fetch_loading"
            :disabled="!selected_erp_template" @click="loadERPTemplate">
            <v-icon left>mdi-cloud-download</v-icon>
            Load Template
          </v-btn>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="erp_dialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    </div>
  </v-container>
</template>

<script>
import axios from "axios";
import VueBarcode from "vue-barcode";
import QrcodeVue from "qrcode.vue";

export default {
  components: { VueBarcode, QrcodeVue },
  data() {
    return {
      update_dialog: false,
      selected_update_template: null,
      draggingLayerIndex: null,
      template_name: "",
      template_name_dialog: false,
      save_template_loading: false,
      upload_image_loading: false,
      activePage: "front",
      drawer: true,
      dragging: null,
      resizing: null,
      selectedElement: null,
      fonts: ["Arial", "Roboto", "Open Sans", "Lato", "Montserrat"],
      dpi: 96,
      pages: {
        front: {
          card: { width: 54, height: 86, bgColor: "#fff", bgImage: "" },
          elements: [],
        },
        back: {
          card: { width: 54, height: 86, bgColor: "#eee", bgImage: "" },
          elements: [],
        },
      },
      history: [],
      historyIndex: -1,
      placeholderFields: [
        { text: "PRN No", value: "{{PRN}}" },
        { text: "Standard", value: "{{Standard}}" },
        { text: "Standard Display Name", value: "{{std_display_name}}" },
        { text: "Roll no", value: "{{rollno}}" },
        { text: "Division", value: "{{division}}" },
        { text: "Name As Per Aadhar", value: "{{name_as_per_aadhar}}" },
        { text: "Emergency Contact Number", value: "{{emergency_contact_number}}" },
        { text: "Mother Full Name", value: "{{mother_full_name}}" },
        { text: "Father Full Name", value: "{{father_full_name}}" },
        { text: "Mother Mobile No", value: "{{mother_mobile_no}}" },
        { text: "Father Mobile No", value: "{{father_mobile_no}}" },
        { text: "Local Address", value: "{{local_address}}" },
        { text: "Permanent Address", value: "{{permanent_address}}" },
        { text: "Gr. No.", value: "{{gr_no}}" },
        { text: "Academic Year", value: "{{ay}}" },
        { text: "School Name", value: "{{school_name}}" },
        { text: "Trust Logo", value: "{{trust_logo}}" },
        { text: "School Logo", value: "{{school_logo}}" },
        { text: "School Photo", value: "{{school_photo}}" },
        { text: "Trust Name", value: "{{trust_name}}" },
        { text: "School Address", value: "{{school_address}}" },
        { text: "School Pin", value: "{{pin}}" },
        { text: "School Phone", value: "{{phone}}" },
        { text: "School Email", value: "{{email}}" },
        { text: "Headmaster Sign", value: "{{headMasterSign}}" },
        { text: "Blood Group", value: "{{stud_blood_group}}" },
        { text: "Date of Birth", value: "{{stud_dob}}" },
        { text: "Father Email", value: "{{father_email}}" },
        { text: "Student Photo", value: "{{PHOTOURL}}" },
        { text: "Student Full Name", value: "{{student_full_name}}" },
        { text: "Student Unicode Name", value: "{{unicode_stud_name}}" },
        { text: "Unicode Division", value: "{{unicode_div}}" },
        { text: "Unicode Standard", value: "{{unicode_std}}" },
        { text: "School name by medium", value: "{{school_name_by_medium}}" },
        { text: "Trust name by medium", value: "{{trust_name_by_medium}}" },
        { text: "School Address by medium", value: "{{school_address_by_medium}}" },
        { text: "School Phone by medium", value: "{{school_phone_by_medium}}" },
        { text: "Primary Contact", value: "{{primary_contact_number}}" },
        { text: "Unicode Local Address", value: "{{unicode_local_address}}" },
        { text: "Unicode Permanent Address", value: "{{unicode_permanent_address}}" },
        { text: "Udise No.", value: "{{udise_no}}" },
        { text: "Affiliation No.", value: "{{cbsc_no}}" },
        { text: "School No.", value: "{{school_no}}" },
        { text: "School Website", value: "{{website}}" },
        { text: "Employee Designation", value: "{{emp_designation}}" },
        { text: "Employee Name", value: "{{emp_name}}" },
        { text: "Employee Address", value: "{{emp_address}}" },
        { text: "Employee DOB", value: "{{emp_dob}}" },
        { text: "Employee Contact", value: "{{emp_phone}}" },
        { text: "Employee Code", value: "{{emp_code}}" },
        { text: "Employee Photo", value: "{{employee_photo}}" },
        { text: "Employee Sign", value: "{{employee_sign}}" },
        { text: "Employee DOJ", value: "{{emp_doj}}" },
        { text: "Employee Blood Group", value: "{{emp_blood_group}}" },
        { text: "Employee Emergency Contact", value: "{{emp_emergency_contact}}" },
        { text: "Employee Department", value: "{{emp_department}}" },
        { text: "Employee National Code", value: "{{emp_national_code}}" },
        { text: "Student Email", value: "{{stud_email}}" },
        { text: "Student Mobile No.", value: "{{stud_mob_no}}" },
      ],
      selectedPlaceholder: "",
      upload_bg_loading: false,
      new_dialog: false,
      erp_dialog: false,
      template_list: [],
      selected_erp_template: null,
      fetch_loading: false,
      snackbar_msg: "",
      color: "",
      snackbar: false,
      isallowed: false
    };
  },
  computed: {
    currentCard() {
      return this.pages[this.activePage].card;
    },
    canvasStyle() {
      return {
        width: this.currentCard.width + "mm",
        height: this.currentCard.height + "mm",
        position: "relative",
        border: "1px solid #ccc",
        backgroundColor: this.currentCard.bgColor,
        backgroundImage: this.currentCard.bgImage
          ? "url(" + this.currentCard.bgImage + ")"
          : "none",
        backgroundSize: "cover",
        margin: "auto",
        overflow: "hidden",
      };
    },
  },
  watch: {
    activePage() {
      this.selectedElement = null;
      this.drawer = true;
    },
  },
  mounted() {
    this.dpi = this.getDPI();
    window.addEventListener("mousemove", this.onDrag);
    window.addEventListener("mouseup", this.stopDragResize);
    this.checkAccess();
  },
  beforeDestroy() {
    window.removeEventListener("mousemove", this.onDrag);
    window.removeEventListener("mouseup", this.stopDragResize);
  },
  methods: {
    checkAccess() {
      const data = {
        functionality : "Generate Icard"
      }
      axios.post("/IDCard/checkIcardAccess",data)
        .then(res => {
          if (res.data.flag == "YES") {
            this.isallowed = res.data.isallowed;
            this.saveHistory();
            this.new_dialog = true;
          }  else if (res.data.flag == "No") {
            this.isallowed = res.data.isallowed;
            this.showSnackbar(res.data.snackbar, "error")
            this.load = false
          }else {
            window.console.log(res.data.msg);
          }
        });
    },
    openUpdateTemplateDialog() {
      this.update_dialog = true;
      axios.get("/IDCard/getIdcardTemplateList").then((res) => {
        if (res.data.msg === "200") {
          this.template_list = res.data.template_list;
        }
      });
    },
    updateExistingTemplate() {
      if (!this.selected_update_template) {
        this.showSnackbar("Select a template to update", "red");
        return;
      }
      const combinedHTML = this.buildIDCardHTML();
      const params = {
        id: this.selected_update_template,
        json_data: JSON.stringify(this.pages, null, 2),
        html_data: combinedHTML,
        template_name: this.template_name,
      };
      this.save_template_loading = true;
      axios
        .post("/IDCard/updateTemplate", params)
        .then((res) => {
          this.save_template_loading = false;
          if (res.data.msg === "200") {
            this.showSnackbar("Template updated successfully!", "green");
            this.update_dialog = false;
            this.template_name_dialog = false;
          } else {
            this.showSnackbar(res.data.msg, "red");
          }
        })
        .catch(() => {
          this.save_template_loading = false;
          this.showSnackbar("Error updating template", "red");
        });
    },
    loadERPTemplate() {
      if (!this.selected_erp_template) return;
      this.fetch_loading = true;
      axios
        .post("/IDCard/getIDCard", { id: this.selected_erp_template })
        .then((res) => {
          this.fetch_loading = false;
          if (res.data.msg === "200" && res.data.template) {
            let jsonData = JSON.parse(res.data.template.template_json);
            if (jsonData.pages) {
              this.pages = jsonData.pages;
            } else if (jsonData.front && jsonData.back) {
              this.pages.front = jsonData.front;
              this.pages.back = jsonData.back;
            } else {
              this.showSnackbar("Invalid JSON format", "red");
              return;
            }
            this.selectedElement = null;
            this.activePage = "front";
            this.saveHistory();
            this.showSnackbar("Template loaded from ERP!", "green");
            this.erp_dialog = false;
            this.new_dialog = false;
          } else {
            this.showSnackbar(res.data.msg, "red");
          }
        })
        .catch(() => {
          this.fetch_loading = false;
          this.showSnackbar("Error loading template", "red");
        });
    },
    loadFromERP() {
      this.erp_dialog = true;
      axios
        .get("/IDCard/getIdcardTemplateList")
        .then((res) => {
          if (res.data.msg === "200") {
            this.template_list = res.data.template_list;
          } else {
            this.showSnackbar(res.data.msg, "red");
          }
        })
        .catch(() => {
          this.showSnackbar("Error loading ERP templates", "red");
        });
    },
    createNewDesign() {
      this.new_dialog = false;
    },
    toggleVisibility(el) {
      el.hidden = !el.hidden;
      this.saveHistory();
    },
    dragStart(index) {
      this.draggingLayerIndex = index;
    },
    dragEnter(index) {
      if (this.draggingLayerIndex === index) return;
      const list = this.pages[this.activePage].elements;
      const movedItem = list.splice(this.draggingLayerIndex, 1)[0];
      list.splice(index, 0, movedItem);
      this.draggingLayerIndex = index;
      this.saveHistory();
    },
    dragEnd() {
      this.draggingLayerIndex = null;
    },
    moveLayerUp(el) {
      const arr = this.pages[this.activePage].elements;
      const index = arr.indexOf(el);
      if (index < arr.length - 1) {
        const temp = arr[index];
        arr[index] = arr[index + 1];
        arr[index + 1] = temp;
      }
      this.saveHistory();
    },
    moveLayerDown(el) {
      const arr = this.pages[this.activePage].elements;
      const index = arr.indexOf(el);
      if (index > 0) {
        const temp = arr[index];
        arr[index] = arr[index - 1];
        arr[index - 1] = temp;
      }
      this.saveHistory();
    },
    removeBackground() {
      this.currentCard.bgImage = "";
      this.saveHistory();
      this.showSnackbar("Background removed", "green");
    },
    async handleBackgroundUpload(e) {
      const file = e.target.files[0];
      if (!file) return;
      if (!file.type.startsWith("image/")) {
        this.showSnackbar("Please select a valid image file.", "red");
        return;
      }
      this.upload_bg_loading = true;
      try {
        const reader = new FileReader();
        reader.onload = (event) => {
          this.currentCard.bgImage = event.target.result;
          this.upload_bg_loading = false;
          this.showSnackbar("Background image added!", "green");
          this.saveHistory();
        };
        reader.readAsDataURL(file);
      } catch (err) {
        console.error(err);
        this.showSnackbar("Error uploading background image", "red");
      } finally {
        this.upload_bg_loading = false;
      }
    },
    insertPlaceholder() {
      if (!this.selectedElement || this.selectedElement.type !== "text") return;
      if (!this.selectedPlaceholder) return;
      this.selectedElement.text += " " + this.selectedPlaceholder;
      this.selectedPlaceholder = null;
      this.saveHistory();
    },
    getDPI() {
      const div = document.createElement("div");
      div.style.width = "1in";
      div.style.height = "1in";
      div.style.position = "absolute";
      div.style.top = "-100%";
      document.body.appendChild(div);
      const dpi = div.offsetWidth;
      document.body.removeChild(div);
      return dpi;
    },
    pxToMm(px) {
      return (px / this.dpi) * 25.4;
    },
    getElementStyle(el) {
      if (el.hidden) {
        return { display: "none" };
      }
      const isSelected = this.selectedElement && this.selectedElement.id === el.id;
      const base = {
        position: "absolute",
        top: el.top + "mm",
        left: el.left + "mm",
        width: el.width + "mm",
        height: el.height + "mm",
        opacity: el.opacity != null ? el.opacity : 1,
        cursor: "move",
        userSelect: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transform: "rotate(" + (el.rotation || 0) + "deg)",
        transformOrigin: "center center",
        boxShadow: isSelected ? "0 0 0 2px #1976d2" : "none",
        backgroundColor: "transparent",
      };

      if (el.type === "shape") {
        const isLine = el.shapeType === "line";
        return Object.assign({}, base, {
          border: isLine ? "none" : el.borderWidth + "px solid " + el.borderColor,
          backgroundColor: isLine ? el.borderColor : el.fillColor,
          borderRadius: el.shapeType === "circle" ? "50%" : (el.borderRadius || 0) + "px",
        });
      }

      if (el.type === "image") {
        return Object.assign({}, base, {
          border: (el.borderWidth || 0) + "px solid " + (el.borderColor || "transparent"),
          borderRadius: (el.borderRadius || 0) + "px",
          overflow: "hidden",
          backgroundColor: el.background || "transparent",
        });
      }

      if (el.type === "text") {
        return Object.assign({}, base, {
          border: "1px solid transparent",
          color: el.color,
          fontSize: el.fontSize + "px",
          fontWeight: el.bold ? "bold" : "normal",
          fontFamily: el.fontFamily,
          textAlign: el.align || "center",
          backgroundColor: el.background || "transparent",
        });
      }

      if (el.type === "barcode") {
        return Object.assign({}, base, {
          border: isSelected
            ? "2px dashed #1976d2"
            : (el.borderWidth || 0) + "px solid " + (el.borderColor || "transparent"),
          backgroundColor: el.background || "transparent",
          borderRadius: (el.borderRadius || 0) + "px",
          overflow: "hidden",
        });
      }

      if (el.type === "qrcode") {
        return Object.assign({}, base, {
          border: isSelected ? "2px dashed #1976d2" : "none",
          backgroundColor: "transparent",
          overflow: "hidden",
        });
      }

      return base;
    },
    textStyle(el) {
      let justify = "center";
      if (el.alignment === "left") justify = "flex-start";
      else if (el.alignment === "right") justify = "flex-end";

      let alignItems = "center";
      if (el.verticalAlignment === "top") alignItems = "flex-start";
      else if (el.verticalAlignment === "bottom") alignItems = "flex-end";

      return {
        color: el.color,
        fontSize: el.fontSize + "px",
        fontWeight: el.bold ? "bold" : "normal",
        fontFamily: el.fontFamily,
        textAlign: el.alignment || "center",
        textDecoration: el.underline ? "underline" : "none",
        display: "flex",
        alignItems: alignItems,
        fontStyle: el.italic ? "italic" : "normal",
        justifyContent: justify,
        width: "100%",
        height: "100%",
        textTransform: el.textTransform || "none",
      };
    },
    canvasClick(e) {
      if (e.target === e.currentTarget) {
        this.selectedElement = null;
        this.drawer = true;
        this.saveHistory();
      }
    },
    startDrag(e, el) {
      if (this.resizing) return;
      this.dragging = {
        el,
        startX: e.clientX,
        startY: e.clientY,
        origTop: el.top,
        origLeft: el.left,
      };
      this.selectedElement = el;
      this.drawer = true;
    },
    startResize(e, el) {
      this.resizing = {
        el,
        startX: e.clientX,
        startY: e.clientY,
        origWidth: el.width,
        origHeight: el.height,
      };
      e.preventDefault();
      e.stopPropagation();
      this.selectedElement = el;
      this.drawer = true;
    },
    onDrag(e) {
      if (this.dragging) {
        const deltaX = e.clientX - this.dragging.startX;
        const deltaY = e.clientY - this.dragging.startY;
        this.dragging.el.left = this.dragging.origLeft + this.pxToMm(deltaX);
        this.dragging.el.top = this.dragging.origTop + this.pxToMm(deltaY);
      } else if (this.resizing) {
        const deltaX = e.clientX - this.resizing.startX;
        const deltaY = e.clientY - this.resizing.startY;
        this.resizing.el.width = this.resizing.origWidth + this.pxToMm(deltaX);
        this.resizing.el.height = this.resizing.origHeight + this.pxToMm(deltaY);
      }
    },
    stopDragResize() {
      this.dragging = null;
      this.resizing = null;
      this.saveHistory();
    },
    selectElement(el) {
      this.selectedElement = el;
      this.drawer = true;
      this.saveHistory();
    },
    addBarcode() {
      if (!this.activePage || !this.pages[this.activePage]) {
        console.error("No active page selected or invalid page reference.");
        return;
      }
      const newBarcode = {
        id: Date.now(),
        type: "barcode",
        text: "1234567890",
        top: 10,
        left: 10,
        width: 20,
        height: 10,
        rotation: 0,
        opacity: 1,
        borderColor: "#000000",
        borderWidth: 0,
        borderRadius: 2,
        barColor: "#000000",
        bgColor: "#ffffff",
        displayValue: false,
        barWidth: 0.5,
        barHeight: 20,
        align: "center",
        background: "transparent",
      };
      this.pages[this.activePage].elements.push(newBarcode);
      this.selectedElement = newBarcode;
      this.saveHistory();
    },
    addQRCode() {
      const qr = {
        id: Date.now(),
        type: "qrcode",
        text: "https://example.com",
        top: 10,
        left: 10,
        width: 20,
        height: 20,
        rotation: 0,
        opacity: 1,
        qrSize: 100,
        qrColor: "#000000",
        bgColor: "#ffffff",
        hidden: false,
      };
      this.pages[this.activePage].elements.push(qr);
      this.selectedElement = qr;
      this.saveHistory();
    },
    addText() {
      this.pages[this.activePage].elements.push({
        id: Date.now(),
        type: "text",
        text: "Text",
        top: 5,
        left: 5,
        width: 30,
        height: 10,
        color: "#000",
        fontSize: 12,
        bold: false,
        italic: false,
        underline: false,
        fontFamily: "Arial",
        opacity: 1.0,
        borderRadius: 0,
        rotation: 0,
        textTransform: "none",
        hidden: false,
        alignment: "center",
        verticalAlignment: "center",
      });
      this.saveHistory();
    },
    addShape(shapeType) {
      const baseShape = {
        id: Date.now(),
        type: "shape",
        shapeType: shapeType,
        top: 10,
        left: 10,
        width: shapeType === "line" ? 40 : 30,
        height: shapeType === "line" ? 2 : 20,
        fillColor: "#1976d2",
        borderColor: "#000000",
        borderWidth: 1,
        borderRadius: 0,
        opacity: 1,
        rotation: 0,
      };
      this.pages[this.activePage].elements.push(baseShape);
      this.saveHistory();
    },
    showSnackbar(msg, clr) {
      this.snackbar = true;
      this.color = clr;
      this.snackbar_msg = msg;
    },
    async handleFile(e) {
      const file = e.target.files[0];
      if (!file) return;
      if (!file.type.startsWith("image/")) {
        this.showSnackbar("Please select a valid image file.", "red");
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64Url = event.target.result;
        this.pages[this.activePage].elements.push({
          id: Date.now(),
          type: "image",
          src: base64Url,
          tag: "Normal",
          top: 5,
          left: 5,
          width: 40,
          height: 30,
          fit: "cover",
          opacity: 1.0,
          borderColor: "#000",
          borderWidth: 0,
          borderRadius: 0,
          rotation: 0,
          hidden: false,
        });
        this.showSnackbar("Image added directly!", "green");
        this.saveHistory();
      };
      reader.onerror = () => {
        this.showSnackbar("Error reading file.", "red");
      };
      reader.readAsDataURL(file);
    },
    buildIDCardHTML() {
      let combinedHTML = '<div class="idcard-container" style="display:flex; flex-direction:column;">';
      const sides = ["front", "back"];
      const allElements = [
        ...this.pages.front.elements,
        ...this.pages.back.elements,
      ];
      const elementsJSON = JSON.stringify(allElements);

      sides.forEach((side) => {
        const page = this.pages[side];
        const bgStyle =
          "background:" +
          page.card.bgColor +
          " url(" +
          (page.card.bgImage || "") +
          ") no-repeat center center;" +
          "background-size:cover;width:" +
          page.card.width +
          "mm;height:" +
          page.card.height +
          "mm;" +
          "position:relative;page-break-after:always;";

        combinedHTML += '<div class="page" style="' + bgStyle + '">';

        page.elements.forEach((el) => {
          const rotation = el.rotation
            ? "transform:rotate(" + el.rotation + "deg);transform-origin:center;"
            : "";

          if (el.type === "text") {
            let justify = "center";
            let textAlign = "center";
            if (el.alignment === "left") {
              justify = "flex-start";
              textAlign = "left";
            } else if (el.alignment === "right") {
              justify = "flex-end";
              textAlign = "right";
            }
            let alignItems = "center";
            if (el.verticalAlignment === "top") alignItems = "flex-start";
            else if (el.verticalAlignment === "bottom") alignItems = "flex-end";
            const textTransform = el.textTransform || "none";

            combinedHTML +=
              "<div style=\"" +
              "position:absolute;" +
              "top:" + el.top + "mm;" +
              "left:" + el.left + "mm;" +
              "width:" + el.width + "mm;" +
              "height:" + el.height + "mm;" +
              "display:flex;" +
              "align-items:" + alignItems + ";" +
              "font-style:" + (el.italic ? "italic" : "normal") + ";" +
              "justify-content:" + justify + ";" +
              "text-decoration:" + (el.underline ? "underline" : "none") + ";" +
              "font-size:" + el.fontSize + "px;" +
              "font-weight:" + (el.bold ? "bold" : "normal") + ";" +
              "font-family:" + el.fontFamily + ";" +
              "color:" + el.color + ";" +
              "opacity:" + (el.opacity || 1) + ";" +
              "text-align:" + textAlign + ";" +
              "text-transform:" + textTransform + ";" +
              "border-radius:" + (el.borderRadius || 0) + "px;" +
              rotation +
              "\">" +
              el.text +
              "</div>";
          } else if (el.type === "image") {
            const tagAttr = el.tag ? 'data-tag="' + el.tag + '"' : "";
            combinedHTML +=
              "<img " + tagAttr + ' src="' + el.src + '" style="' +
              "position:absolute;" +
              "top:" + el.top + "mm;" +
              "left:" + el.left + "mm;" +
              "width:" + el.width + "mm;" +
              "height:" + el.height + "mm;" +
              "object-fit:" + (el.fit || "cover") + ";" +
              "opacity:" + (el.opacity || 1) + ";" +
              "border-radius:" + (el.borderRadius || 0) + "px;" +
              "border:" + (el.borderWidth || 0) + "px solid " + (el.borderColor || "#000") + ";" +
              rotation +
              '">';
          } else if (el.type === "shape") {
            const border =
              el.shapeType === "line"
                ? "none"
                : el.borderWidth + "px solid " + el.borderColor;
            const radius =
              el.shapeType === "circle" ? "50%" : (el.borderRadius || 0) + "px";
            const bg = el.shapeType === "line" ? el.borderColor : el.fillColor;

            combinedHTML +=
              "<div style=\"position:absolute;top:" + el.top + "mm;left:" + el.left + "mm;" +
              "width:" + el.width + "mm;height:" + el.height + "mm;border:" + border + ";border-radius:" + radius + ";" +
              "background-color:" + bg + ";opacity:" + (el.opacity || 1) + ";" + rotation + "\"></div>";
          } else if (el.type === "barcode") {
            combinedHTML +=
              '<div class="barcode-box" style="' +
              "position:absolute;" +
              "top:" + el.top + "mm;" +
              "left:" + el.left + "mm;" +
              "width:" + el.width + "mm;" +
              "height:" + el.height + "mm;" +
              "overflow:hidden;" +
              rotation +
              '">' +
              '<svg id="barcode-' + el.id + '" style="width:100%; height:100%; display:block;"></svg>' +
              "</div>";
          } else if (el.type === "qrcode") {
            combinedHTML +=
              '<div id="qr-' + el.id + '" style="' +
              "position:absolute;" +
              "top:" + el.top + "mm;" +
              "left:" + el.left + "mm;" +
              "width:" + el.width + "mm;" +
              "height:" + el.height + "mm;" +
              rotation +
              '"></div>';
          }
        });

        combinedHTML += "</div>";
      });

      combinedHTML += "</div>";

      combinedHTML +=
        "<scri" + "pt src=\"https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js\"></scr" + "ipt>" +
        "<scri" + "pt src=\"https://cdn.jsdelivr.net/npm/qrcodejs@1.0.0/qrcode.min.js\"></scr" + "ipt>" +
        "<scri" + "pt>" +
        "document.addEventListener(\"DOMContentLoaded\",function(){" +
        "var DPI=" + this.dpi + ";" +
        "function mmToPx(mm){ return (mm/25.4)*DPI; }" +
        "var ALL=" + elementsJSON + ";" +
        "if(window.JsBarcode){" +
        "document.querySelectorAll(\"svg[id^='barcode-']\").forEach(function(svg){" +
        "var id=svg.id.replace(\"barcode-\",\"\");" +
        "var el=ALL.find(function(e){return e.id==id;});" +
        "if(el){" +
        "JsBarcode(svg,el.text||\"\",{" +
        "format:\"CODE128\"," +
        "width:el.barWidth||2," +
        "height:mmToPx(el.height||10)," +
        "displayValue:el.displayValue||false," +
        "lineColor:el.barColor||\"#000\"," +
        "background:el.bgColor||\"#fff\"," +
        "margin:0" +
        "});" +
        "}" +
        "});" +
        "}" +
        "if(window.QRCode){" +
        "document.querySelectorAll(\"div[id^='qr-']\").forEach(function(el){" +
        "var id=el.id.replace(\"qr-\",\"\");" +
        "var q=ALL.find(function(e){return e.id==id;});" +
        "if(q){" +
        "el.innerHTML=\"\";" +
        "new QRCode(el,{" +
        "text:q.text||\"\"," +
        "width:Math.round(mmToPx(q.width))," +
        "height:Math.round(mmToPx(q.height))," +
        "useSVG:true," +
        "correctLevel:QRCode.CorrectLevel.H," +
        "colorDark:q.qrColor||\"#000\"," +
        "colorLight:q.bgColor||\"#fff\"" +
        "});" +
        "}" +
        "});" +
        "}" +
        "});" +
        "</scr" + "ipt>";

      const html =
        "<html>" +
        "<head>" +
        '<meta charset="UTF-8">' +
        "<title>Print ID Card</title>" +
        "<style>" +
        "@page { size: " + this.pages.front.card.width + "mm " + this.pages.front.card.height + "mm; margin: 0; }" +
        "@media print {" +
        "html, body { width:" + this.pages.front.card.width + "mm; height:" + this.pages.front.card.height + "mm; }" +
        ".page { width:" + this.pages.front.card.width + "mm; height:" + this.pages.front.card.height + "mm; page-break-after: always; }" +
        "}" +
        "body { margin:0; padding:0; display:flex; flex-direction:column; align-items:center;" +
        "-webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; background:#fff; }" +
        ".page { position:relative; overflow:hidden; background-size:cover; }" +
        "img { display:block; }" +
        "</style>" +
        "</head>" +
        "<body>" + combinedHTML + "</body>" +
        "</html>";

      return html;
    },
    saveTemplate() {
      if (!this.template_name) {
        this.showSnackbar("Template name is required", "red");
        return;
      }
      const combinedHTML = this.buildIDCardHTML();
      const params = {
        template_name: this.template_name,
        json_data: JSON.stringify(this.pages, null, 2),
        html_data: combinedHTML,
      };
      this.save_template_loading = true;
      axios
        .post("/IDCard/saveTemplate", params)
        .then((res) => {
          this.save_template_loading = false;
          if (res.data.msg == "200")
            this.showSnackbar("Template saved successfully!", "green");
          else this.showSnackbar(res.data.msg, "red");
        })
        .catch((err) => {
          this.save_template_loading = false;
          this.showSnackbar("Error saving template", "red");
          console.error(err);
        });
    },
    printIDCard() {
      const printWindow = window.open("", "_blank");
      const htmlContent = this.buildIDCardHTML();
      printWindow.document.write(
        "<html>" +
        "<head>" +
        "<title>Print ID Card</title>" +
        "<style>" +
        "@page { size: " + this.pages.front.card.width + "mm " + this.pages.front.card.height + "mm; margin: 0; }" +
        "body { margin:0; padding:0; display:flex; flex-direction:column; align-items:center;" +
        "-webkit-print-color-adjust: exact; print-color-adjust: exact; }" +
        ".page { position:relative; page-break-after:always; }" +
        "img { display:block; }" +
        "</style>" +
        "</head>" +
        "<body>" + htmlContent + "</body>" +
        "</html>"
      );
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    },
    exportIDCard() {
      const blob = new Blob([JSON.stringify(this.pages, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "id_card.json";
      a.click();
      URL.revokeObjectURL(url);
    },
    importIDCard(event) {
      const file = event.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        let data = JSON.parse(e.target.result);
        if (data.pages) this.pages = data.pages;
        else if (data.front && data.back) {
          this.pages.front = data.front;
          this.pages.back = data.back;
        } else {
          alert("Invalid ID card JSON");
          return;
        }
        this.selectedElement = null;
        this.activePage = "front";
        this.saveHistory();
      };
      reader.readAsText(file);
      this.new_dialog = false;
    },
    deleteElement() {
      if (!this.selectedElement) return;
      const elements = this.pages[this.activePage].elements;
      const index = elements.findIndex((el) => el.id === this.selectedElement.id);
      if (index !== -1) {
        elements.splice(index, 1);
        this.selectedElement = null;
        this.saveHistory();
      }
    },
    saveHistory() {
      if (this.historyIndex < this.history.length - 1) {
        this.history = this.history.slice(0, this.historyIndex + 1);
      }
      const snapshot = JSON.parse(JSON.stringify(this.pages));
      this.history.push(snapshot);
      if (this.history.length > 50) this.history.shift();
      this.historyIndex = this.history.length - 1;
    },
    undo() {
      if (this.historyIndex <= 0) return;
      this.historyIndex--;
      this.pages = JSON.parse(JSON.stringify(this.history[this.historyIndex]));
      this.selectedElement = null;
    },
    redo() {
      if (this.historyIndex >= this.history.length - 1) return;
      this.historyIndex++;
      this.pages = JSON.parse(JSON.stringify(this.history[this.historyIndex]));
      this.selectedElement = null;
    },
    open_save_template_dialog() {
      this.template_name_dialog = true;
    },
  },
};
</script>

<style scoped>
.designer-element {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  user-select: none;
}

.resize-handle {
  position: absolute;
  width: 10px;
  height: 10px;
  right: 0;
  bottom: 0;
  background: #1976d2;
  cursor: se-resize;
}

.size-display {
  position: absolute;
  top: 0px;
  opacity: 0.2;
  left: 0;
  background: #fff;
  padding: 2px 4px;
  border: 1px solid #1976d2;
  font-size: 7px;
}

.modern-drawer {
  background: #fafafa;
  border-left: 1px solid #e0e0e0;
}

.v-navigation-drawer .v-btn {
  transition: all 0.25s ease;
}

.v-navigation-drawer .v-btn:hover {
  background-color: #eeeeee;
}

.layer-item {
  padding: 4px 6px;
  background: #fafafa;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-bottom: 4px;
  cursor: grab;
}

.layer-item:active {
  cursor: grabbing;
}

.layer-item.active {
  background: #d0e7ff !important;
  border: 1px solid #1976d2;
}

.modern-btn {
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 0.3px;
  transition: all 0.2s ease;
}

.modern-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.15);
}
</style>